import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import { FormulaParts } from "../../ModelLedgerIsland/_FormulaParts";
import { formulaParts, shownValueParts } from "../../ModelLedgerIsland/ledgerFormat";
import { isTwinLocal } from "@/zapi-frontend/shared/lib/rqaSim/twin";
import { anchorRect, offscreen, place } from "./figureCardPlacement";
import type { TPlacement } from "./figureCardPlacement";
import { memoType } from "../memoTypography";
import { cn } from "@/zapi-frontend/shared/lib/utils";

export const FIGURE_CARD_ID = "figure-card";

type TFigureCardProps = {
  figure: TLedgerFigure;
  anchor: HTMLElement;
  byKey: Map<string, TLedgerFigure>;
  onAway: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
};

const ledgerSearch = (search: string, key: string) => {
  const params = new URLSearchParams(search);
  params.set("tab", "overview");
  params.set("view", "modelLedger");
  params.set("figure", key);
  params.delete("chapterId");
  return params.toString();
};

// A ledger figure beside the number in the prose that quotes it: the name, the value as the prose
// writes it, the key that lands on that row, and the arithmetic or the inputs behind it.
export function FigureCard({ figure, anchor, byKey, onAway, onPointerEnter, onPointerLeave }: TFigureCardProps) {
  const { t } = useTranslation("projectHome");
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<TPlacement | null>(null);

  const update = useCallback(() => {
    const card = ref.current;
    if (!card) return;
    const viewport = { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight };
    const rect = anchorRect(anchor);
    if (!rect || offscreen(rect, viewport)) {
      onAway();
      return;
    }
    const next = place(rect, card.getBoundingClientRect(), viewport);
    setAt((prev) => (prev && prev.x === next.x && prev.y === next.y && prev.maxH === next.maxH ? prev : next));
  }, [anchor, onAway]);

  useLayoutEffect(update);

  useEffect(() => {
    let frame = 0;
    const onMove = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [update]);

  const value = shownValueParts(anchor.textContent ?? "", figure);
  const local = isTwinLocal(figure);
  const formula = figure.formula ? formulaParts(figure.formula) : null;
  const inputs = !figure.formula ? figure.dependsOn : [];
  const reveal = (key: string) => navigate({ pathname: location.pathname, search: ledgerSearch(location.search, key) });

  return createPortal(
    <div
      ref={ref}
      id={FIGURE_CARD_ID}
      role="dialog"
      aria-label={figure.name}
      tabIndex={-1}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className="fixed left-0 top-0 z-1000 rounded-md border border-innoveraZinc-150 bg-white px-3.5 py-3 shadow-lg"
      style={{
        width: "max-content",
        maxWidth: "min(23rem, calc(100vw - 1.5rem))",
        transform: `translate3d(${at?.x ?? 0}px, ${at?.y ?? 0}px, 0)`,
        maxHeight: at ? `${at.maxH}px` : undefined,
        overflowY: "auto",
        visibility: at ? "visible" : "hidden"
      }}
    >
      <div className={memoType.cardName}>{figure.name}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={memoType.cardValue}>{value.number}</span>
        {value.qualifier && <span className={memoType.cardQualifier}>{value.qualifier}</span>}
      </div>
      {local ? (
        <div className={cn("mt-1.5 wrap-anywhere", memoType.cardKey)}>{figure.key}</div>
      ) : (
        <Link
          to={{ pathname: location.pathname, search: ledgerSearch(location.search, figure.key) }}
          className={cn(
            "mt-1.5 block",
            memoType.cardKey,
            "underline decoration-innoveraZinc-300 decoration-dotted underline-offset-2 wrap-anywhere hover:text-innoveraZinc-900"
          )}
        >
          {figure.key}
        </Link>
      )}

      {(formula || inputs.length > 0) && (
        <div className="mt-3 border-t border-innoveraZinc-150 pt-2.5">
          {formula ? (
            <div className="flex flex-wrap content-start items-center gap-y-2">
              <FormulaParts parts={formula} byKey={byKey} onReveal={reveal} />
            </div>
          ) : (
            <>
              <div className={memoType.cardOverline}>{t("modelLedger.figureCardDependsOn")}</div>
              <div className={cn("mt-1.5", memoType.cardInputs)}>
                {inputs.map((key) => (
                  <div key={key} className="wrap-anywhere">
                    {key}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {figure.group && !local && (
        <div className={cn("mt-3 border-t border-innoveraZinc-150 pt-2.5", memoType.cardGroup)}>{figure.group}</div>
      )}
    </div>,
    document.body
  );
}
